/*
 * ******************************************************
 * Copyright (c) 2022 Jenke & Rauschert GbR <info@aiandor.de> - All Rights Reserved
 *
 * This file is part of a software product owned by the Jenke & Rauschert GbR.
 * Unauthorized distribution of this file is strictly prohibited.
 * ******************************************************
 */

import {Breadcrumb, Card, Empty, Result, Skeleton} from "antd";
import {Link, useParams} from "react-router-dom";
import {Customer, Device} from "../core/datamodel";
import React, {useEffect, useState} from "react";
import {ViewState} from "../states";
import {get} from "../core/request";
import 'dayjs/locale/de';
import {MonthReportEditor, YearReportEditor, MonthV1ReportEditor, YearV1ReportEditor} from "../components/reportEditor";
import {useAppSelector} from "../store";

export default function DeviceReportView(props: any) {
    const params: { id: string } = useParams();
    const [device, setDevice] = useState({} as Device);
    const [state, setState] = useState(ViewState.initial);
    const [activeTabKey, setActiveTabKey] = useState<string>('Monatsbericht');

    const [customer, setCustomer] = useState({} as Customer);
    const customerReloadSignal = useAppSelector(state => state.reloadSignals.customer)

    const v2_types_month = ["CT", "MG", "NM", "RF", "XR"]
    const v2_types_year = ["CT", "MG", "NM", "RF", "XR"]


    useEffect(() => {
        setState(ViewState.loading);
        get("devices/" + params.id).done((response) => {
                setDevice(new Device(response.data.device));
                setState(ViewState.success);
            }
        ).fail(() => setState(ViewState.failed))
    }, [params.id])


    useEffect(() => {
        if (!!device?.props?.customer?.props?.uuid) {
            get("customers/" + device.props.customer.props.uuid).done(
                (response) => {
                    setCustomer(new Customer(response.data.customer));
                }
            )
        }
    }, [device?.props?.customer, customerReloadSignal])


    const tabList = [
        {
            key: 'Monatsbericht',
            tab: 'Monatsbericht',
        },
        {
            key: 'Jahresbericht',
            tab: 'Jahresbericht',
        },
    ];

    const breadcrumb = (
        <Breadcrumb style={{paddingTop: 6, paddingBottom: 6}}>
            <Breadcrumb.Item><Link to="/">Home</Link></Breadcrumb.Item>
            <Breadcrumb.Item><Link to="/customers">Kunden</Link></Breadcrumb.Item>
            <Breadcrumb.Item><Link
                to={"/customers/" + customer.props?.uuid}>{!!customer.props?.repr ? customer.props.repr : "Unknown"}</Link></Breadcrumb.Item>
            <Breadcrumb.Item><Link
                to={"/devices/" + params.id}>{!!device?.props?.name ? device.props.name : "Unknown"}</Link></Breadcrumb.Item>
            <Breadcrumb.Item>Bericht</Breadcrumb.Item>
        </Breadcrumb>
    )

    const card = (children: any) => (
        <Card
            tabList={tabList}
            onTabChange={key => setActiveTabKey(key)}
            activeTabKey={activeTabKey}
            children={children}
        />
    )

    const tabs: Record<string, React.ReactNode> = {
        'Monatsbericht': <MonthReportEditor device={device}/>,
        'Jahresbericht': <YearReportEditor device={device}/>,
    }

    switch (state) {
        case ViewState.initial:
            return (
                <>
                    {breadcrumb}
                    {card(<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={"Noch keine Berichte abgerufen"}/>)}
                </>
            )

        case ViewState.loading:
            return (
                <>
                    {breadcrumb}
                    {card(<Skeleton active/>)}
                </>
            )

        case ViewState.success:
            return (
                <>
                    {breadcrumb}
                    {card(tabs[activeTabKey])}
                </>
            )

        case ViewState.failed:
        default:
            return (
                <>
                    {breadcrumb}
                    {card(
                        <Result
                            status="500"
                            title="Fehler"
                            subTitle="Entschuldigung, die Berichte konnten nicht geladen werden!"
                        />
                    )}
                </>
            )
    }
}